import { useState, useEffect, useMemo } from 'react';
import ButtonPrimary from '../../components/style-components/ButtonPrimary';
import { useDispatch, useSelector } from 'react-redux';
import { createCoupon, reset, getCouponById, updateCoupon } from '../../features/coupons/couponSlice';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getProducts } from '../../features/products/productSlice';

function AdminAddCoupon() {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { couponId } = useParams();
    const { coupon, coupon_orders, isError, isLoading, message } = useSelector((state) => state.coupon);
    const { products } = useSelector((state) => state.product);

    const { user } = useSelector((state) => state.auth);

    useEffect(() => {
      if (!user) {
        navigate('/');
      }
    }, [user, navigate]);

    const generateCode = () => {
        return Math.random().toString(36).substring(2, 8).toUpperCase();
    };

    const [code, setCode] = useState(couponId ? '' : generateCode());
    const [active, setActive] = useState(true);
    const [delivery, setDelivery] = useState(true);
    const [discountAmount, setDiscountAmount] = useState('');
    const [discountPercentage, setDiscountPercentage] = useState('');
    const [expiryDate, setExpiryDate] = useState('');
    const [productList, setProductList] = useState([]);
    const [categoryList, setCategoryList] = useState([]);
    const [restrictionType, setRestrictionType] = useState('none'); // none | products | categories
    const [maxDiscountQuantity, setMaxDiscountQuantity] = useState('');
    const [useLimit, setUseLimit] = useState('');

    const [addingMany, setAddingMany] = useState(false);
    const [couponCount, setCouponCount] = useState(2);

    const categories = useMemo(() => Array.from(new Set(products.map(product => product.category).filter(Boolean))), [products]);

    const handleRestrictionChange = (value) => {
        setRestrictionType(value);
        if (value === 'none') {
            setCategoryList([]);
            setProductList([]);
            return;
        }
        if (value === 'products') {
            setCategoryList([]);
            if (productList.length === 0 && !couponId && products.length > 0) {
                setProductList(products.map(product => product._id));
            }
        } else {
            setProductList([]);
        }
    };

    useEffect(() => {
        if (isError) {
            toast.error(message);
        }

        if (coupon) {
            const hasCategories = coupon.categories && coupon.categories.length > 0;
            const hasProducts = coupon.products && coupon.products.length > 0;
            const chosenRestriction = hasCategories ? 'categories' : hasProducts ? 'products' : 'none';
            setRestrictionType(chosenRestriction);
            setCode(coupon.code);
            setActive(coupon.active);
            setDelivery(coupon.delivery);
            setDiscountAmount(coupon.discountAmount ? (coupon.discountAmount / 100).toFixed(2) : '');
            setDiscountPercentage(coupon.discountPercentage ?? '');
            setExpiryDate(coupon.expiryDate.split('T')[0]);
            setProductList(hasCategories ? [] : (coupon.products ? coupon.products.map(product => product._id) : []));
            setCategoryList(hasCategories ? coupon.categories : []);
            setMaxDiscountQuantity(coupon.maxDiscountQuantity ?? '');
            setUseLimit(coupon.useLimit);
        }

        return () => {
            dispatch(reset());
        };
    }, [coupon, message, dispatch, isError]);

    useEffect(() => {
        if (couponId) {
            dispatch(getCouponById(couponId));
        }
    }, [couponId, dispatch]);

    useEffect(() => {
        if (!couponId && products.length > 0 && restrictionType === 'products') {
            setProductList(products.map(product => product._id));
        }
    }, [products, couponId, categories, restrictionType]);

    useEffect(() => {
        dispatch(getProducts());
    }, [dispatch]);

    const validateCoupon = () => {
        const hasAmount = discountAmount !== '' && Number(discountAmount) !== 0;
        const hasPercentage = discountPercentage !== '' && Number(discountPercentage) !== 0;

        if (!code) {
            toast.error('Kupona kods ir obligāts');
            return false;
        }
        if (!hasAmount && !hasPercentage) {
            toast.error('Atlaide ir obligāta');
            return false;
        }
        if (hasAmount && hasPercentage) {
            toast.error('Izvēlies tikai vienu atlaides veidu');
            return false;
        }
        if (!expiryDate) {
            toast.error('Derīguma termiņš ir obligāts');
            return false;
        }
        if (restrictionType === 'products' && productList.length === 0) {
            toast.error('Izvēlies vismaz vienu produktu');
            return false;
        }
        if (restrictionType === 'categories' && categoryList.length === 0) {
            toast.error('Izvēlies vismaz vienu kategoriju');
            return false;
        }
        return true;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!validateCoupon()) {
            return;
        }
        const normalizedDiscountAmount = discountAmount !== '' ? Math.round(Number(discountAmount) * 100) : null;
        const normalizedDiscountPercentage = discountPercentage !== '' ? Number(discountPercentage) : null;

        const couponData = {
            _id: couponId,
            code,
            active,
            delivery,
            discountAmount: normalizedDiscountAmount,
            discountPercentage: normalizedDiscountPercentage,
            expiryDate,
            products: restrictionType === 'products' ? productList : [],
            categories: restrictionType === 'categories' ? categoryList : [],
            maxDiscountQuantity,
            useLimit,
            numOfCoupons: addingMany ? couponCount : 1,
        };

        if (couponId) {
            dispatch(updateCoupon(couponData));
        } else {
            dispatch(createCoupon(couponData));
        }
        navigate('/edit-coupons');
    };

    return (
        <div className='add-coupon'>
            <ButtonPrimary text="Atpakaļ" onClick={() => navigate('/edit-coupons')}/>
            <h2 className='light padding-10-top'>{couponId ? "Rediģēt kuponu" : "Pievienot kuponu"}</h2>
            {coupon_orders && coupon_orders.length > 0 && 
                <div className="flex column gap-10 padding-10-top">
                    <p className="bold">Kupons izmantots:</p>
                    <ul>
                        {coupon_orders.map(order => (
                            <li className='pointer' key={order._id}><a href={`/edit-orders/${order._id}`} target='_blank'>{order._id}</a></li>
                        ))}
                    </ul>
                </div>
            }
            {!couponId && 
                <div className="product-checkbox">
                    <input className='checkbox' type="checkbox" checked={addingMany} onChange={(e) => setAddingMany(e.target.checked)}/>
                    <label>Izveidot vairākus</label>
                </div>
            }
            
            {addingMany && 
                <div className="couponForm flex column gap-10 padding-10-top">
                    <label>Cik kuponus izveidot?</label>
                    <input type="number" value={couponCount} onChange={(e) => setCouponCount(e.target.value)}/>
                </div>
            }
            <form className='couponForm padding-10-top' onSubmit={handleSubmit}>
                <div className="product-checkbox">
                    <input className='checkbox' type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)}/>
                    <label>Aktīvs</label>
                </div>
                <div className="product-checkbox">
                    <input className='checkbox' type="checkbox" checked={delivery} onChange={(e) => setDelivery(e.target.checked)}/>
                    <label>Bezmaksas piegāde</label>
                </div>
                <input
                    type="text"
                    placeholder="Code"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                />
                <input
                    type="number"
                    placeholder="Summas atlaide"
                    value={discountAmount}
                    onChange={(e) => setDiscountAmount(e.target.value)}
                />
                <input
                    type="number"
                    placeholder="Procentuāla atlaide"
                    value={discountPercentage}
                    onChange={(e) => setDiscountPercentage(e.target.value)}
                />
                <input
                    type="date"
                    value={expiryDate}
                    onChange={(e) => setExpiryDate(e.target.value)}
                />
                <input type="number" placeholder="Atļautais lietojuma skaits (tukšs = bez limita)" value={useLimit} onChange={(e) => setUseLimit(e.target.value)}/>
                <input
                    type="number"
                    placeholder="Atlaides vienību limits (tukšs = visām vienībām)"
                    value={maxDiscountQuantity}
                    onChange={(e) => setMaxDiscountQuantity(e.target.value)}
                />
                <label className='bold padding-10-top'>Kupona ierobežojums:</label>
                <div className="product-checkbox">
                    <input
                        type="radio"
                        name="restrictionType"
                        checked={restrictionType === 'none'}
                        onChange={() => handleRestrictionChange('none')}
                    />
                    <label>Visam grozam</label>
                </div>
                <div className="product-checkbox">
                    <input
                        type="radio"
                        name="restrictionType"
                        checked={restrictionType === 'products'}
                        onChange={() => handleRestrictionChange('products')}
                    />
                    <label>Pēc produktiem</label>
                </div>
                <div className="product-checkbox">
                    <input
                        type="radio"
                        name="restrictionType"
                        checked={restrictionType === 'categories'}
                        onChange={() => handleRestrictionChange('categories')}
                    />
                    <label>Pēc kategorijām</label>
                </div>

                {restrictionType === 'categories' && (
                    <>
                        <label className='bold padding-10-top'>Atļautās kategorijas:</label>
                        {categories && categories.length > 0 ? categories.map((category) => (
                            <div key={category} className="product-checkbox">
                                <input
                                    type="checkbox"
                                    checked={categoryList.includes(category)}
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            setCategoryList([...categoryList, category]);
                                        } else {
                                            setCategoryList(categoryList.filter(item => item !== category));
                                        }
                                    }}
                                />
                                <label>{category}</label>
                            </div>
                        )) : (
                            <p>Nav pieejamu kategoriju</p>
                        )}
                    </>
                )}

                {restrictionType === 'products' && (
                    <>
                        <label className='bold padding-10-top'>Atļautie produkti:</label>
                        {products && products.map((product) => (
                            <div key={product._id} className="product-checkbox">
                                <input
                                    type="checkbox"
                                    checked={productList.includes(product._id)}
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            setProductList([...productList, product._id]);
                                        } else {
                                            setProductList(productList.filter(id => id !== product._id));
                                        }
                                    }}
                                />
                                <label>{product.title}</label>
                            </div>
                        ))}
                    </>
                )}
                
                <ButtonPrimary text={couponId ? "Saglabāt kuponu" : "Pievienot kuponu"} buttonType="submit" type="submit"/>
                <div className="padding-10-bottom"></div>
            </form>
        </div>
    );
}

export default AdminAddCoupon;
